import { createContext, useContext, useState, useEffect, PropsWithChildren } from "react";

import { sortWithNumberParsing } from "./utils.tsx";
import { AppConfigurationContext } from './appConfigurationProvider.tsx';
import { UserDto } from "./basicApi.ts";
import { BeamlineDto, ProposalDto, EsafDto, getUsers, getBeamlines, getProposals, getEsafs } from './api/api.ts';


// This is a "context provider" React component for commonly used metadata.
// It keeps its own internal cache of records and provides a hook for invalidating them.

// It's designed to be placed once in the DOM, below the AppConfigurationProvider,
// and usually below the currentUserProvider as well.

// This design allows relatively free access to the cache nywhere,
// but of course creates the usual performance problem of the data being monolithic.
// If we find ourselves constantly making small updates to, say, Proposal viewer membership,
// and the list from the server has grown beyond 10,000 records, we might need to redesign.


// We'll provide access to the records we cache in several ways:
interface CatalogById<Data> {
  idsInDisplayOrder: number[];
  byId: Map<number, Data>;
}

// A 'slug' is a unique string identifier, often used in URLs, in this case derived from the name of the record.
interface CatalogBySlug<Data> {
  slugsInDisplayOrder: string[];
  bySlug: Map<string, Data>;
}

// This component should be provided with a boolean value indicating whether login has
// occurred.  If the value is false, the component will skip loading,
// then try again as soon as the value is changed.
interface ProviderProps {
  loggedIn: boolean;
}

enum MetaDataLoadingState { NotTriggered, Pending, Succeeded, Failed };

// The structure we are providing to components in the hierarchy below the provider
interface MetadataInterface {
  // Access to the records we cache
  users: CatalogById<UserDto>;
  beamlines: CatalogBySlug<BeamlineDto>;
  proposals: CatalogBySlug<ProposalDto>;
  esafs: CatalogBySlug<EsafDto>;

  // General loading state, succeeds when all others succeed.
  loadingState: MetaDataLoadingState;  // Track the status of loading chunks of the data we provide
  // A callback for when a child component makes a change that affects these records.
  changed: () => void;
  // This counter is incremented whenever the data is changed.
  // Components can watch this counter to know when they need to re-render,
  // or compare existing cached data to check for a re-render.
  // This is an old hack, and is actually more straightforward than providing a
  // registration point for callbacks and then guessing whether the calls are stale or duplicates.
  changeCounter: number;
}

const MetadataContext = createContext<MetadataInterface>({
                    users: {idsInDisplayOrder:[],byId:new Map()},
                    beamlines: {slugsInDisplayOrder:[],bySlug:new Map()},
                    proposals: {slugsInDisplayOrder:[],bySlug:new Map()},
                    esafs: {slugsInDisplayOrder:[],bySlug:new Map()},

                    loadingState: MetaDataLoadingState.NotTriggered,
                    changed: () => {},
                    changeCounter: 0
                  });

const MetadataProvider: React.FC<PropsWithChildren<ProviderProps>> = (props) => {
  const [loggedIn, setLoggedIn] = useState<boolean>(props.loggedIn);

  const appConfig = useContext(AppConfigurationContext);
  
  const [users, setUsers] = useState<CatalogById<UserDto>>({idsInDisplayOrder:[],byId:new Map()});
  const [beamlines, setBeamlines] = useState<CatalogBySlug<BeamlineDto>>({slugsInDisplayOrder:[],bySlug:new Map()});
  const [proposals, setProposals] = useState<CatalogBySlug<ProposalDto>>({slugsInDisplayOrder:[],bySlug:new Map()});
  const [esafs, setEsafs] = useState<CatalogBySlug<EsafDto>>({slugsInDisplayOrder:[],bySlug:new Map()});

  const [loadingState, setLoadingState] = useState<MetaDataLoadingState>(MetaDataLoadingState.NotTriggered);

  const [changeCounter, setChangeCounter] = useState<number>(0);


  useEffect(() => {
    appConfig.log('MetadataProvider mounted');
    return () => {
      appConfig.log('MetadataProvider unmounted');
    };
  }, []);


  useEffect(() => {
    // Proceed only if our watched values equal these:
    if (loadingState != MetaDataLoadingState.Pending) { return; }

    const fetchData = async () => {
      try {
        // Use Promise.all to ask for everything at once.
        const [usersResult, beamlinesResult, proposalsResult, esafsResult] = await Promise.all([
          getUsers(),
          getBeamlines(),
          getProposals(),
          getEsafs()
        ]);

        if (!usersResult.success || !beamlinesResult.success || !proposalsResult.success || !esafsResult.success) {
          const message = `Error fetching metadata: ${usersResult.message} ${beamlinesResult.message} ${proposalsResult.message} ${esafsResult.message}`;
          appConfig.log(message);
          setLoadingState(MetaDataLoadingState.Failed);
          return;
        }

        let userCatalog:CatalogById<UserDto> = {idsInDisplayOrder:[],byId:new Map()}
        usersResult.response!.results.forEach((u) => {
          userCatalog.idsInDisplayOrder.push(u.id);
          userCatalog.byId!.set(u.id, u);
        });
        userCatalog.idsInDisplayOrder.sort((a, b) => userCatalog.byId.get(a)!.username.localeCompare(userCatalog.byId.get(b)!.username));

        // These are all prepared the same way - organized by slug, and sorted by name.
        let [beamlinesCatalog, proposalsCatalog, esafsCatalog] =
          [beamlinesResult, proposalsResult, esafsResult].map((result, index) => {
            let catalog:CatalogBySlug<any> = {slugsInDisplayOrder:[],bySlug:new Map()};
            result.response!.results.forEach((r) => {
              catalog.slugsInDisplayOrder.push(r.slug);
              catalog.bySlug!.set(r.slug, r);
            });
            catalog.slugsInDisplayOrder.sort((a, b) => sortWithNumberParsing(catalog.bySlug.get(a)!.name, catalog.bySlug.get(b)!.name));
            return catalog;
          });

        setUsers(userCatalog);
        setBeamlines(beamlinesCatalog);
        setProposals(proposalsCatalog);
        setEsafs(esafsCatalog);

        setLoadingState(MetaDataLoadingState.Succeeded);
        return;

      } catch (error) {
        appConfig.log('Error fetching metadata:', error);
      }
      setLoadingState(MetaDataLoadingState.Failed);
    };

    // Call fetchData when our overall state changes to 'pending'
    fetchData();
    appConfig.log('Called fetchData');

  }, [loadingState]);


  useEffect(() => {
    if (!loggedIn) {
      // If the status goes to false, we don't actually do anything.
      // If it changes to true, we act.
      appConfig.log('MetadataProvider thinks user is not logged in.');
      return;
    }
    appConfig.log('MetadataProvider thinks user is logged in');
    setLoadingState(MetaDataLoadingState.Pending);
  }, [loggedIn]);


  // A callback for when a child component makes a change to the SampleConfigurationSets instance.
  // Note: In the future it may be prudent to pass a set ID into this,
  // to trigger separate server data update calls for each set, since each will have its own history.
  function changed() {
    appConfig.log("Called changed() in MetadataProvider.");
    setChangeCounter(changeCounter + 1);
  };


  return (
    <MetadataContext.Provider value={{
        users: users,
        beamlines: beamlines,
        proposals: proposals,
        esafs: esafs,

        loadingState: loadingState,
        changed: changed,
        changeCounter: changeCounter
    }}>
    {props.children}
    </MetadataContext.Provider>
  )
}

export { MetadataContext, MetadataProvider, MetaDataLoadingState }